import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableContainer from "@mui/material/TableContainer"
import { Button, Typography } from "@mui/material"
import { Fragment, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import { IconButton } from "@mui/material"
import Box from "@mui/material/Box"

const IndicatorPerRegion = (props) => {
  const { indicators_per_region } = props
  return (
    <Fragment>
      <DialogTitle>Indicators Per Region</DialogTitle>
      <TableContainer style={{ padding: "10px" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <b>Region</b>
              </TableCell>
              <TableCell>
                <b>Unserved</b>
              </TableCell>
              <TableCell>
                <b>Served</b>
              </TableCell>
              <TableCell>
                <b>% Served</b>
              </TableCell>
              <TableCell>
                <b>Total Population</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(indicators_per_region).map((row_key, i) => {
              const row = indicators_per_region[row_key]
              const demand = row.unserved + row.served
              return (
                <TableRow key={i}>
                  <TableCell>{row_key}</TableCell>
                  <TableCell>{row.unserved.toLocaleString()}</TableCell>
                  <TableCell>{row.served.toLocaleString()}</TableCell>
                  <TableCell>
                    {((row.served / demand) * 100).toLocaleString()}
                  </TableCell>
                  <TableCell>{demand.toLocaleString()}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Fragment>
  )
}

const PopulationPerDistance = (props) => {
  const { indicator_per_distance } = props
  return (
    <Fragment>
      <DialogTitle>Population per Distance Band</DialogTitle>
      <TableContainer style={{ padding: "10px" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Distance Bands (m)</TableCell>
              {Object.keys(
                indicator_per_distance[Object.keys(indicator_per_distance)[0]],
              ).map((key, c) => {
                return <TableCell key={"c" + c}>{key}</TableCell>
              })}
              <TableCell>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(indicator_per_distance).map((distance_band, r) => {
              const row = indicator_per_distance[distance_band]
              let row_total = 0
              Object.keys(row, r).forEach((key) => {
                row_total += parseInt(row[key])
              })
              return (
                <TableRow key={r}>
                  <TableCell>
                    {parseInt(distance_band).toLocaleString()}
                  </TableCell>
                  {Object.keys(row).map((key, c) => {
                    return (
                      <TableCell key={"r" + r + "c" + c}>{row[key]}</TableCell>
                    )
                  })}
                  <TableCell>{row_total}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Fragment>
  )
}

const get_column_totals = (table) => {
  const rows = Object.keys(table)
  const columns = Object.keys(table[rows[0]])
  let row_total = {}
  columns.forEach((column) => {
    let column_total = 0
    rows.forEach((row) => {
      column_total += table[row][column]
    })
    row_total[column] = column_total
  })
  return row_total
}

const find_band = (distance, distance_breaks) => {
  let return_value = undefined
  distance_breaks.forEach((distance_break) => {
    if (distance < distance_break && return_value === undefined) {
      return_value = distance_break
    }
  })
  if (!return_value) {
    return_value = distance_breaks[distance_breaks.length - 1]
  }
  return parseInt(return_value)
}

const calculate_indicators = (analysis_units) => {
  if (Object.keys(analysis_units[0].properties).includes("served") === false) {
    return null
  }

  const distance_breaks = [3500, 5000, 10000, 15000, 1000000]
  const unique_densities = [
    ...new Set(
      analysis_units.map((analysis_unit) => analysis_unit.properties.density),
    ),
  ]
  const unique_regions = [
    ...new Set(
      analysis_units.map((analysis_unit) => analysis_unit.properties.region),
    ),
  ]
  let indicators = {
    indicators_per_density: {},
    indicators_per_region: {},
    indicators_per_density_region: {},
    indicator_per_distance: {},
  }
  unique_regions.forEach((region) => {
    indicators.indicators_per_density_region[region] = {}
  })
  unique_densities.forEach((density) => {
    indicators.indicators_per_density[density] = {
      served: 0,
      unserved: 0,
    }
    unique_regions.forEach((region) => {
      indicators.indicators_per_region[region] = {
        served: 0,
        unserved: 0,
      }
      indicators.indicators_per_density_region[region][density] = {
        served: 0,
        unserved: 0,
      }
    })
  })
  distance_breaks.forEach((distance_break) => {
    indicators.indicator_per_distance[parseInt(distance_break)] =
      Object.fromEntries(unique_densities.map((density) => [density, 0]))
  })

  analysis_units.forEach((analysis_unit) => {
    const { properties } = analysis_unit
    const { served, unserved, region, density, distance_to_nearest_facility } =
      properties
    const demand = served + unserved

    const distance_band = find_band(
      distance_to_nearest_facility,
      distance_breaks,
    )
    indicators.indicators_per_density[density].served += served
    indicators.indicators_per_density[density].unserved += unserved
    indicators.indicators_per_region[region].served += served
    indicators.indicators_per_region[region].unserved += unserved
    indicators.indicators_per_density_region[region][density].served += served
    indicators.indicators_per_density_region[region][density].unserved +=
      unserved
    indicators.indicator_per_distance[distance_band][density] += demand
  })

  indicators.indicators_per_density.Total = get_column_totals(
    indicators.indicators_per_density,
  )
  indicators.indicators_per_region.Total = get_column_totals(
    indicators.indicators_per_region,
  )

  //Drop Decimals from Results
  let rounded_indicators = JSON.parse(
    JSON.stringify(indicators),
    (key, value) =>
      typeof value === "number" && key !== "lat" && key !== "lng"
        ? Math.floor(value)
        : value,
  )

  //Make negative values zero
  rounded_indicators = JSON.parse(
    JSON.stringify(rounded_indicators),
    (key, value) => (typeof value === "number" && value < 0 ? 0 : value),
  )

  return rounded_indicators
}

const IndicatorDialog = (props) => {
  const { au_geo_json } = props

  const [open, set_open_dialog] = useState(false)

  const handleClose = () => {
    set_open_dialog(false)
  }

  let indicators
  try {
    indicators = au_geo_json ? calculate_indicators(au_geo_json.features) : null
  } catch (error) {
    console.log(error)
  }

  return (
    <Fragment>
      <Button fullWidth onClick={() => set_open_dialog(true)}>
        Open Indicator Stats
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth={"md"}
      >
        <DialogTitle sx={{ justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center">
            <Box flexGrow={1}>Indicators</Box>
            <Box>
              <IconButton
                onClick={() => {
                  set_open_dialog(false)
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {indicators ? (
            <div>
              <IndicatorPerRegion
                indicators_per_region={indicators.indicators_per_region}
              />
              <PopulationPerDistance
                indicator_per_distance={indicators.indicator_per_distance}
              />
            </div>
          ) : (
            <Typography>No indicator information to display.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Fragment>
  )
}

export default IndicatorDialog
